import {
	PogisDocument,
	Regulation,
	RegulatoryDocument,
	Requirement,
} from 'types';
import React, {useMemo, useRef} from 'react';
import {useState} from 'react';
import {
	CommandButtonCompat as CommandButton,
	LinkCompat as Link,
	LabelCompat as Label,
	SeparatorCompat as Separator,
	StackCompat as Stack,
	ShimmerCompat as Shimmer,
	SpinnerCompat as Spinner,
} from 'components/FluentUICompat';
import {
	ArrayValues,
	renderAttachmentsPogis,
	renderPhase,
	StatusField,
} from 'components/EntityList/ColumnRenderers';

import i18n from 'i18n/i18n';
import {useTranslation} from 'react-i18next';
import {formatDateTime} from 'i18n/localeDateFormat';
import {DEFAULT_DATE} from 'components/EntityList/EntityUtils';
import {useGetRegulationResultCardDocumentsLazyQuery} from '../../hooks';
import {v4 as uuidv4} from 'uuid';

type SearchResultProps = {
	item: Regulation | Requirement | RegulatoryDocument | PogisDocument;
	infoSectionExpanded?: boolean;
};

export const SearchResultRegulation: React.FC<SearchResultProps> = props => {
	const [getRegDocs, {loading: loadingRegDoc, data: dataRegDoc}] =
		useGetRegulationResultCardDocumentsLazyQuery();

	const {item} = props;

	const infoSection = <InfoSectionRegulation {...props} />;

	const compRegDoc = useMemo(() => {
		return loadingRegDoc ? (
			<Spinner />
		) : (
			<Stack>
				{(dataRegDoc?.regulation?.regulatoryDocuments ?? []).map(x => (
					<Link
						key={uuidv4()}
						href={`/regulatoryDocuments/${x.id}`}
						target='_blank'
						style={{paddingLeft: 35}}
					>
						{x.name}
					</Link>
				))}
			</Stack>
		);
	}, [item, dataRegDoc, loadingRegDoc]);

	const handleExpandRegDoc = () => {
		if (!dataRegDoc) {
			getRegDocs({variables: {regulation: item.id}});
		}
	};

	const sideSection = (
		<SearchResultSideSection
			sectionName={`Vorschriftenversionen`}
			compExpanded={compRegDoc}
			handleExpansion={handleExpandRegDoc}
		/>
	);

	const propsResult = {infoSection, sideSection};
	return <SearchResultTemplate {...propsResult} />;
};

export const SearchResultRegulatoryDocument: React.FC<
	SearchResultProps
> = props => {
	const infoSection = <InfoSectionRegulatoryDocument {...props} />;

	const propsResult = {infoSection};
	return <SearchResultTemplate {...propsResult} />;
};

export const SearchResultRequirement: React.FC<SearchResultProps> = props => {
	const infoSection = <InfoSectionRequirement {...props} />;

	const propsResult = {infoSection};
	return <SearchResultTemplate {...propsResult} />;
};

export const SearchResultPOGIS: React.FC<SearchResultProps> = props => {
	const {t} = useTranslation('features/pogisdocuments', {
		keyPrefix: 'PogisDocumentsList',
	});

	const {item} = props;

	const pogisDocument = item as PogisDocument;

	const infoSection = <InfoSectionPOGIS {...props} />;

	const compAttach = useMemo(() => {
		return <>{renderAttachmentsPogis()(pogisDocument)}</>;
	}, [item]);

	const sideSection = (
		<SearchResultSideSection
			sectionName={t('Attachments')}
			compExpanded={compAttach}
		/>
	);

	const propsResult = {infoSection, sideSection};
	return <SearchResultTemplate {...propsResult} />;
};

export const InfoSectionRegulation: React.FC<SearchResultProps> = props => {
	const {t} = useTranslation('features/regulations', {
		keyPrefix: 'RegulationDetail',
	});

	const {item, infoSectionExpanded} = props;

	const regulation = item as Regulation;

	const htmlInfo = useMemo(
		() => (regulation.summary ?? '').replaceAll('</p><p>', ''),
		[item],
	);

	const compLink = useMemo(
		() => (
			<Link href={`/regulations/${regulation.id}`} target='_blank'>
				<Label>
					<HighlightedText text={regulation.regulationNumber} />
				</Label>
				<HighlightedText text={regulation.name} />
			</Link>
		),
		[item],
	);

	const compExpanded = useMemo(() => {
		return (
			<>
				<Separator />
				<table>
					{getTableRow(t('MarketsLabel'), getArray(regulation.markets))}
					{getTableRow(
						t('MainKeywordsLabel'),
						getArray(regulation.mainKeywords),
					)}
					{getTableRow(t('KeyWordsLabel'), getArray(regulation.keywords))}
					{getTableRow(
						t('StandardPlusPcmsClustersLabel'),
						getArray(regulation.standardPlusPcmsClusters),
					)}
					{getTableRow(
						t('ContactPersonLabel'),
						getArray(regulation.contactPersons),
					)}
				</table>
			</>
		);
	}, [item]);

	return (
		<SearchResultInfoSection
			htmlInfo={htmlInfo}
			compLink={compLink}
			compExpanded={compExpanded}
			infoSectionExpanded={infoSectionExpanded}
		/>
	);
};

export const InfoSectionRegulatoryDocument: React.FC<
	SearchResultProps
> = props => {
	const nsTranslation = 'features/regulatorydocuments';
	const {t} = useTranslation(nsTranslation, {
		keyPrefix: 'ParagraphsList',
	});

	const {t: tStatus} = useTranslation(nsTranslation, {
		keyPrefix: 'RegulatoryDocumentsList',
	});

	const {item, infoSectionExpanded} = props;

	const regDoc = item as RegulatoryDocument;

	const htmlInfo = useMemo(
		() => (regDoc.summary ?? '').replaceAll('</p><p>', ''),
		[item],
	);

	const compLink = useMemo(
		() => (
			<Link href={`/regulatoryDocuments/${regDoc.id}`} target='_blank'>
				<Stack horizontal>
					<span style={{paddingTop: 5}}>
						{regDoc.regulation?.regulationNumber ?? ''}&nbsp;|&nbsp;
					</span>
					<Label>
						<HighlightedText text={regDoc.name} />
					</Label>
				</Stack>
				<span>{regDoc.regulation?.name ?? ''} </span>
			</Link>
		),
		[item],
	);

	const compExpanded = useMemo(() => {
		const statusComponent = (
			<StatusField
				enumName={'RegulatoryDocumentStatus'}
				item={regDoc}
				value={regDoc.status}
			></StatusField>
		);
		const effComponent = formatDateTime(new Date(regDoc.dateEffective), i18n);
		const nfComponent = formatDateTime(
			new Date(regDoc.dateNewRegistration),
			i18n,
		);
		const ntComponent = formatDateTime(new Date(regDoc.dateNewTypes), i18n);
		const colPI = {
			key: 'phaseIn',
			name: t('PhaseIn'),
			fieldName: 'phaseIn',
			minWidth: 120,
		};
		const piComp = renderPhase()(regDoc, 0, colPI);
		const colPO = {
			key: 'phaseOut',
			name: t('PhaseOut'),
			fieldName: 'phaseOut',
			minWidth: 120,
		};
		const poComp = renderPhase()(regDoc, 0, colPO);
		return (
			<>
				<Separator />
				<table>
					{getTableRow(t('Keywords'), getArray(regDoc.keywords))}
					{getTableRow(
						t('VehicleCategory'),
						getArray(regDoc.vehicleCategories),
					)}
					{getTableRow(t('DriveVariants'), getArray(regDoc.driveVariants))}
					{getTableRow(tStatus('Status'), statusComponent)}
					{getTableRowNullable(
						t('DateEffective'),
						effComponent,
						isDefined(regDoc.dateEffective),
					)}
					{getTableRowNullable(
						t('ModelYear'),
						regDoc.modelYear,
						isDefined(regDoc.modelYear),
					)}
					{getTableRowNullable(
						t('DateNewRegistration'),
						nfComponent,
						isDefined(regDoc.dateNewRegistration),
					)}
					{getTableRowNullable(
						t('DateNewTypes'),
						ntComponent,
						isDefined(regDoc.dateNewTypes),
					)}
					{getTableRowNullable(
						t('PhaseIn'),
						piComp,
						regDoc?.phaseIn?.length > 0,
					)}
					{getTableRowNullable(
						t('PhaseOut'),
						poComp,
						regDoc?.phaseOut?.length > 0,
					)}
				</table>
			</>
		);
	}, [item]);

	return (
		<SearchResultInfoSection
			htmlInfo={htmlInfo}
			compLink={compLink}
			compExpanded={compExpanded}
			infoSectionExpanded={infoSectionExpanded}
		/>
	);
};

export const InfoSectionRequirement: React.FC<SearchResultProps> = props => {
	const {t} = useTranslation('features/requirements');

	const {item, infoSectionExpanded} = props;

	const requirement = item as Requirement;

	const htmlInfo = useMemo(() => {
		const definitionHTMLFormatted = (requirement.definition ?? '<p></p>')
			.replaceAll('</p><p>', '<br/>')
			.replaceAll('<br/><br/>', '<br/>');

		return definitionHTMLFormatted.startsWith('<p>')
			? definitionHTMLFormatted
			: `<p>${definitionHTMLFormatted}</p>`;
	}, [item]);

	const compExpanded = useMemo(() => {
		const statusComponent = (
			<StatusField
				enumName={'RequirementStatus'}
				item={requirement}
				value={requirement.status}
			></StatusField>
		);
		return (
			<>
				<Separator />
				<table>
					{getTableRow(t('VexCluster'), getArray(requirement.vexClusters))}
					{getTableRow(t('SystemLevels'), getArray(requirement.systemLevels))}
					{getTableRow(t('CreatedBy'), getArray([requirement.createdBy]))}
					{getTableRow(t('Markets'), getArray(requirement.markets))}
					{getTableRow(t('Keywords'), getArray(requirement.keywords))}
					{getTableRow(t('Status'), statusComponent)}
				</table>
			</>
		);
	}, [item]);

	const compLink = useMemo(
		() => (
			<Link href={`/requirements/${requirement.id}`} target='_blank'>
				<Label>
					<HighlightedText text={requirement.requirementId ?? ''} />
				</Label>
				<span style={{paddingTop: 5}}>
					<HighlightedText text={requirement.name.replaceAll('[temp] ', '')} />
				</span>
			</Link>
		),
		[item],
	);

	return (
		<SearchResultInfoSection
			htmlInfo={htmlInfo}
			compLink={compLink}
			compExpanded={compExpanded}
			infoSectionExpanded={infoSectionExpanded}
		/>
	);
};

export const InfoSectionPOGIS: React.FC<SearchResultProps> = props => {
	const {t} = useTranslation('features/pogisdocuments', {
		keyPrefix: 'PogisDocumentsList',
	});

	const {item, infoSectionExpanded} = props;

	const pogisDocument = item as PogisDocument;

	const {effDate, nfDate, ntDate, myDate} = useMemo(() => {
		const datesTypes = [
			'Effective Date',
			'New Registration',
			'New Types',
			'Model Year',
		];

		const pogisdates = pogisDocument.dates;

		const effDates = pogisdates.filter(x => x.dateType === datesTypes.at(0));
		const nfDates = pogisdates.filter(x => x.dateType === datesTypes.at(1));
		const ntDates = pogisdates.filter(x => x.dateType === datesTypes.at(2));
		const myDates = pogisdates.filter(x => x.dateType === datesTypes.at(3));

		const restDates = pogisdates.filter(x => !datesTypes.includes(x.dateType));

		const effDate = effDates.length > 0 ? effDates[0].date : DEFAULT_DATE;
		const nfDate = nfDates.length > 0 ? nfDates[0].date : DEFAULT_DATE;
		const ntDate = ntDates.length > 0 ? ntDates[0].date : DEFAULT_DATE;
		const myDate = myDates.length > 0 ? myDates[0].date : DEFAULT_DATE;
		return {effDate, nfDate, ntDate, myDate, restDates};
	}, [item]);

	const compExpanded = useMemo(() => {
		const keywordsRow = getTableRowNullable(
			t('Keywords'),
			getArrayPrimitiv(pogisDocument.keywords),
			pogisDocument.keywords.filter(x => (x ?? '').length > 0).length > 0,
		);

		const marketsRow = getTableRowNullable(
			t('Markets'),
			getArrayPrimitiv(pogisDocument.markets),
			pogisDocument.markets.filter(x => (x ?? '').length > 0).length > 0,
		);

		const statusRow = getTableRowNullable(
			t('RegulationStatus'),
			getArrayPrimitiv([pogisDocument.regulationStatus ?? '']),
			(pogisDocument.regulationStatus ?? '').length > 0,
		);

		const effDateRow = getTableRowNullable(
			t('Effective Date'),
			formatDateTime(new Date(effDate), i18n),
			effDate > DEFAULT_DATE,
		);

		const nfDateRow = getTableRowNullable(
			t('NFDate'),
			formatDateTime(new Date(nfDate), i18n),
			nfDate > DEFAULT_DATE,
		);

		const ntDateRow = getTableRowNullable(
			t('NTDate'),
			formatDateTime(new Date(ntDate), i18n),
			ntDate > DEFAULT_DATE,
		);

		const myDateRow = getTableRowNullable(
			t('ModelYear'),
			<Stack horizontal>
				{`${t('ModelYear')}: ${
					t('MY') + new Date(myDate).getFullYear().toString().slice(2, 4)
				}`}
			</Stack>,
			myDate > DEFAULT_DATE,
		);

		return (
			<>
				<Separator />
				<table>
					{keywordsRow}
					{marketsRow}
					{statusRow}
					{effDateRow}
					{nfDateRow}
					{ntDateRow}
					{myDateRow}
				</table>
			</>
		);
	}, [item]);

	const compLink = useMemo(
		() => (
			<Link href={`/pogisArchive?id=${pogisDocument.pogisId}`} target='_blank'>
				<Label>
					<HighlightedText text={'POGIS | ' + pogisDocument.pogisId} />
				</Label>
				{pogisDocument.standards && pogisDocument.standards.length > 0 && (
					<span style={{paddingTop: 5}}>
						<HighlightedText text={pogisDocument.standards.join(', ')} />
					</span>
				)}
			</Link>
		),
		[item],
	);

	return (
		<SearchResultInfoSection
			htmlInfo={`<p>${pogisDocument.shortInfo ?? ''}</p>`}
			compLink={compLink}
			compExpanded={compExpanded}
			infoSectionExpanded={infoSectionExpanded}
		/>
	);
};

export const SearchResultLoading: React.FC = () => {
	const infoSection = (
		<div style={{padding: 10}}>
			<Shimmer />
			<br />
			<Shimmer />
			<br />
			<Shimmer />
		</div>
	);

	const propsResult = {infoSection};
	return <SearchResultTemplate {...propsResult} />;
};

export const SearchResultTemplate: React.FC<{
	infoSection: React.ReactElement;
	sideSection?: React.ReactElement;
}> = ({infoSection, sideSection}) => {
	return (
		<Stack horizontal style={searchResultStyle.body}>
			<Stack.Item style={{width: '45vw'}}>{infoSection}</Stack.Item>
			<Separator vertical />
			<Stack.Item style={{width: '20vw'}}>{sideSection ?? <></>}</Stack.Item>
		</Stack>
	);
};

const SearchResultInfoSection: React.FC<{
	htmlInfo: string;
	compLink: React.ReactElement;
	compExpanded: React.ReactElement;
	infoSectionExpanded?: boolean;
}> = ({htmlInfo, compLink, compExpanded, infoSectionExpanded}) => {
	const {t} = useTranslation('features/search');

	const [isExpanded, setIsExpanded] = useState(infoSectionExpanded === true);

	const refLink = useRef<HTMLDivElement | null>(null);

	const handleExpandSearchResult = (e: React.MouseEvent<HTMLButtonElement>) => {
		refLink.current?.focus();
		setIsExpanded(!isExpanded);
	};

	const iconProps = React.useMemo(
		() => ({iconName: isExpanded ? 'ChevronUp' : 'ChevronDown'}),
		[isExpanded],
	);

	return (
		<>
			<div ref={refLink}>{compLink}</div>
			<HighlightedHTML html={htmlInfo} expanded={isExpanded} />
			{isExpanded && compExpanded}

			<CommandButton
				iconProps={iconProps}
				text={isExpanded ? t('ShowLess') : t('ShowMore')}
				onClick={handleExpandSearchResult}
			/>
		</>
	);
};

const SearchResultSideSection: React.FC<{
	sectionName: string;
	compExpanded: React.ReactElement;
	handleExpansion?: () => void;
}> = ({sectionName, compExpanded, handleExpansion}) => {
	const [isExpandedSide, setIsExpandedSide] = useState(false);

	const iconPropsSide = React.useMemo(
		() => ({iconName: isExpandedSide ? 'ChevronUp' : 'ChevronDown'}),
		[isExpandedSide],
	);

	const handleExpandSide = () => {
		if (handleExpansion) {
			handleExpansion();
		}
		setIsExpandedSide(!isExpandedSide);
	};

	const compSide = isExpandedSide ? compExpanded : <></>;

	return (
		<>
			<CommandButton
				iconProps={iconPropsSide}
				text={sectionName}
				onClick={handleExpandSide}
			/>
			{compSide}
		</>
	);
};

export const HighlightedText: React.FC<{
	text: string;
}> = ({text}) => {
	const displayHTML = (text ?? '')
		.replaceAll('<em>', '<mark>')
		.replaceAll('</em>', '</mark>');

	return (
		<div
			dangerouslySetInnerHTML={{
				__html: displayHTML,
			}}
		/>
	);
};

export const HighlightedHTML: React.FC<{
	html: string;
	expanded: boolean;
}> = ({html, expanded}) => {
	const displayHTML = html
		.replaceAll('<em>', '<mark>')
		.replaceAll('</em>', '</mark>');

	return (
		<div
			style={{
				width: '40vw',
				overflow: 'hidden',
				display: '-webkit-box',
				WebkitBoxOrient: 'vertical',
				WebkitLineClamp: expanded ? 'none' : 1,
				paddingLeft: '10px',
			}}
			dangerouslySetInnerHTML={{
				__html: displayHTML,
			}}
		/>
	);
};

export const searchResultStyle = {
	body: {
		paddingLeft: '10px',
		backgroundColor: 'rgb(255, 255, 255)',
		boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
		margin: 'auto',
		width: '65vw',
		borderRadius: '4px',
	},
};

export const getTableRow = (name: string, child: any) => (
	<tr>
		<td style={{verticalAlign: 'top'}}>{name}: </td>
		<td>{child}</td>
	</tr>
);

export const getTableRowNullable = (
	name: string,
	child: any,
	condition: boolean,
) => (condition ? getTableRow(name, child) : <></>);

export const getArray = (array: any[]) => (
	<ArrayValues
		array={array}
		getValue={arrayitem => <HighlightedText text={arrayitem.name} />}
	></ArrayValues>
);

const getArrayPrimitiv = (array: any[]) => (
	<ArrayValues
		array={array}
		getValue={arrayitem => <HighlightedText text={arrayitem} />}
	></ArrayValues>
);

function isDefined<T>(value: T | null | undefined): value is T {
	return value !== null && value !== undefined;
}
